import type { PolicySection } from '../../types/policy';
import { company as c } from '../company';

/** Sections that follow the coverage table on the Warranty page. */
export const warrantySections: PolicySection[] = [
  {
    id: 'products',
    heading: 'Covered products',
    blocks: [
      `This Limited Warranty is provided by ${c.legalName} to the original retail purchaser of M1 PRO Wireless RGB Lights and Magnetic Mount System bases bought new directly from ${c.website}. Products purchased from unauthorized resellers, marketplaces, auctions or second-hand are not covered.`,
    ],
  },
  {
    id: 'period',
    heading: 'Warranty period',
    blocks: [
      'M1 PRO lights and Magnetic Mount System bases purchased as a single light or a 2-light Duo System carry a 1-Year Limited Warranty from the date of delivery. Lights and bases purchased as part of a 4, 6, 8, 12 or 24-light system carry a Limited Lifetime Warranty, meaning coverage for as long as the original purchaser owns the product. Battery coverage is limited as described below. Coverage is limited to the original purchaser and is not transferable.',
    ],
  },
  {
    id: 'covered',
    heading: 'What is covered',
    blocks: [
      'Defects in materials and workmanship under normal residential use, installed and operated according to the included instructions, including lights that arrive defective or fail to power on, charge or illuminate.',
    ],
  },
  {
    id: 'battery',
    heading: 'Rechargeable battery',
    blocks: [
      `The built-in rechargeable battery is covered against defects for ${c.batteryCoverageYears} year from delivery. Like all lithium batteries, capacity and run time naturally decrease with age, charge cycles and temperature. Gradual reduction in battery capacity or run time is normal wear and is not a defect.`,
    ],
  },
  {
    id: 'exclusions',
    heading: 'What is not covered',
    blocks: [
      {
        list: [
          'Accidental damage, drops, impacts, crushing, or damage from pool cleaners, equipment or pets.',
          'Misuse, abuse, neglect, or use contrary to the instructions or our Product Safety & Use page.',
          'Charging while wet, with a damaged or open port cover, or with non-standard chargers.',
          'Water intrusion caused by an open, damaged or improperly seated USB-C cover, or use beyond the advertised protection rating.',
          'Damage from improper water chemistry, shock treatments, salt systems, chemical exposure, freezing, or extreme heat.',
          'Improper installation, unsupported surfaces, or mounting methods other than those described in the instructions.',
          'Modification, disassembly, or repair by anyone other than us.',
          'Cosmetic wear, discoloration, fading, scratches or calcium/mineral deposits that do not affect function.',
          'Normal battery capacity reduction after the first year.',
          'Commercial, rental or public-facility use.',
          'Lost or stolen products, and products without valid proof of purchase.',
          'Labor, installation, removal, pool draining or diving costs.',
        ],
      },
    ],
  },
  {
    id: 'procedure',
    heading: 'How to make a claim',
    blocks: [
      'Submit a warranty claim through our Warranty Claim page with your order number, a description of the issue, and a photo or short video showing the problem. We may ask you to perform basic troubleshooting (full charge, reset, cover check). Approved claims are resolved as described below. Do not send products to us without a Return Authorization number.',
    ],
  },
  {
    id: 'proof',
    heading: 'Proof of purchase',
    blocks: ['Your order number or original receipt from MarineForceLED is required for every claim.'],
  },
  {
    id: 'shipping',
    heading: 'Shipping for warranty claims',
    blocks: [
      'We pay standard shipping to send replacement products within the United States. In most cases photo or video evidence is enough. If we ask you to return the defective product, we will provide a prepaid label within the first 30 days after delivery; after that, return shipping is your responsibility.',
    ],
  },
  {
    id: 'remedies',
    heading: 'Remedies',
    blocks: [
      'For a covered defect, we will, at our option, replace the product with a new or refurbished unit of the same model, or with a comparable model of equal or better function if the original is unavailable. If replacement is not commercially practicable, we may instead refund the price you paid for the defective unit. Replacement products are covered for the remainder of the original warranty period. THIS IS YOUR SOLE AND EXCLUSIVE REMEDY UNDER THIS WARRANTY.',
    ],
  },
  {
    id: 'limits',
    heading: 'Limitations',
    blocks: [
      'ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE, ARE LIMITED IN DURATION TO THE APPLICABLE WARRANTY PERIOD ABOVE. WE ARE NOT LIABLE FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, INCLUDING DAMAGE TO POOLS, LINERS, FINISHES, BOATS OR OTHER PROPERTY. Some states do not allow limitations on how long an implied warranty lasts or the exclusion of incidental or consequential damages, so these limitations may not apply to you.',
    ],
  },
  {
    id: 'territory',
    heading: 'Where coverage applies',
    blocks: ['This warranty applies to products purchased and used in the United States.'],
  },
  {
    id: 'rights',
    heading: 'Your legal rights',
    blocks: [
      'This warranty gives you specific legal rights, and you may also have other rights which vary from state to state. Disputes under this warranty are subject to the informal-resolution and arbitration provisions of our Terms of Service.',
    ],
  },
  {
    id: 'contact',
    heading: 'Warrantor',
    blocks: [`${c.legalName}${c.mailingAddress ? ` · ${c.mailingAddress}` : ''} · ${c.supportEmail}`],
  },
];
